"use client";
import React, {
  ReactNode,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";
import { useData } from "./context";
import useQueryData from "@libs/customHooks/useQueryData";
import LoadingTableData from "@components/table/loadingScreen";
import SomethingWentWrong from "@components/table/SomethingWentWrong";
import RedirectProvider from "./RedirectProvider";
import ResponsiveDrawer from "@components/sideBar/sidebarUser";
import SessionExpired from "@components/Modals/SessionExpired";
import SessionExpireAlert from "@components/Modals/SessionExpireAlert";

interface props {
  children: ReactNode;
}
const LoggedInProvider = ({ children }: props) => {
  const { data: session, update } = useSession();
  const pathname = usePathname();
  const router = useRouter();
  const [showModal, setShowModal] = useState(false);
  const [showExpireAlertModal, setShowExpireAlertModal] = useState(false);
  const [subheaderText, setSubheaderText] = useState("");

  // Memoize online/offline handlers
  const handleOnline = useCallback(() => {
    console.log("Application is back online. Reloading...");
    // window.location.reload();
  }, []);

  const handleOffline = useCallback(() => {
    console.log("Application is offline.");
    // Implement any offline handling logic here
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      console.log("Application is back online. Reloading...");
      // window.location.reload();
    };

    const handleOffline = () => {
      console.log("Application is offline.");
      // Implement any offline handling logic here
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [handleOnline, handleOffline]);

  const {
    setPermissionData,
    sessionExpired,
    sessionExpireAlert,
    sessionExpireTime,
    setUserData,
    setCurrentRole,
    setCurrentOrg,
  } = useData();

  // Memoize computed values
  const previewOnly = useMemo(
    () =>
      (pathname.includes("/bundles/") || pathname.includes("/project/")) &&
      sessionExpired,
    [pathname, sessionExpired],
  );

  const ignoreSessionExpireAlerts = useMemo(
    () =>
      pathname.includes("/bundles/") ||
      pathname.includes("/project/") ||
      pathname.includes("/verify-invitation/"),
    [pathname],
  );

  useEffect(() => {
    if (sessionExpired && !ignoreSessionExpireAlerts) {
      setShowModal(true);
    }
    if (sessionExpireAlert && !ignoreSessionExpireAlerts && !sessionExpired) {
      setShowExpireAlertModal(true);
      if (sessionExpireTime > 20) {
        setSubheaderText(
          "You will be signed out from the account in next 30 minutes",
        );
      } else {
        setSubheaderText(
          "Your session is going to expire soon. We recommend you to login again",
        );
      }
    }
  }, [
    sessionExpired,
    sessionExpireAlert,
    ignoreSessionExpireAlerts,
    sessionExpireTime,
  ]);

  const { data } = useQueryData({
    queryKey: ["userProfile"],
    endPoint: "/users/profile",
  });

  const {
    data: permissionData,
    error,
    isFetching,
    refetch,
  } = useQueryData({
    queryKey: ["permissionData"],
    endPoint: `/roles/user-permission?orgId=${data?.user?.organisationId}`,
    retry: 1,
    enabled: !!data?.user?.organisationId,
  });

  useEffect(() => {
    if (data) {
      setUserData(data);
    }
  }, [data, setUserData]);

  useEffect(() => {
    if (data) {
      setCurrentRole(
        data?.user?.lastLoggedInRole || data?.user?.internalRoleName?.[0],
      );
      setCurrentOrg({
        id: data?.user?.organisationId,
        name: data?.user?.organisationName,
      });
    }
  }, [data, router, setCurrentRole, setCurrentOrg]);
  // Only update session when data changes, not on every render.
  // Skip on /select-role to avoid a concurrent cookie write that strips selectedRole.
  useEffect(() => {
    if (session && data && !pathname?.includes("/select-role")) {
      update({ ...session, user: { ...session?.user, data } });
    }
  }, [data]); // Removed session and update from deps to prevent infinite loop

  // Memoize permission strings
  const permissionStrings = useMemo(
    () => [
      "fiat-management.wallet.view",
      "project.project.view-list",
      "setup-guide.setup.access",
    ],
    [],
  );

  // Skip session update on /select-role to avoid a concurrent cookie write that strips selectedRole.
  useEffect(() => {
    if (permissionData) {
      setPermissionData(permissionData);

      const finalPermissions = permissionData?.permissions?.filter(
        (item: string) => permissionStrings.includes(item),
      );

      if (session && !pathname?.includes("/select-role")) {
        update({
          ...session,
          user: {
            ...session?.user,
            permissionData: { permissions: finalPermissions },
          },
        });
      }
    }
  }, [permissionData, permissionStrings, setPermissionData]); // Removed session and update

  // handling session expiry
  useEffect(() => {
    if (!session?.user?.token) {
      window.location.reload();
    }
  }, [session?.user?.token]); // Only check token, not entire user object

  const queryParams = useSearchParams();
  const bundleProjectPreviewPage = useMemo(
    () => queryParams.get("type") && queryParams.get("signedURL"),
    [queryParams],
  );
  const projectFromBundle = useMemo(
    () => queryParams.get("type") === "PREVIEW_BUNDLE_PROJECT",
    [queryParams],
  );

  // Memoize the condition check
  const isSpecialRoute = useMemo(
    () =>
      pathname?.includes("/verification") ||
      pathname?.includes("/view-document") ||
      pathname?.includes("/document-viewer") ||
      pathname?.includes("/on-boarding") ||
      pathname?.includes("/select-role") ||
      pathname?.includes("/verify-invitation") ||
      pathname?.includes("/tax-credits/forms/cfcc/45Z-seller-intake-form") ||
      pathname?.includes("supply-intake-form") ||
      bundleProjectPreviewPage ||
      projectFromBundle ||
      previewOnly ||
      !session?.user?.token,
    [
      pathname,
      bundleProjectPreviewPage,
      projectFromBundle,
      previewOnly,
      session?.user?.token,
    ],
  );

  // Memoize handlers
  const handleCloseExpireAlert = useCallback(() => {
    setShowExpireAlertModal(false);
  }, []);

  return (
    <>
      {isSpecialRoute ? (
        <div>{children}</div>
      ) : error ? (
        <div
          className="something-wrong"
          style={{
            backgroundColor: "white !important",
            minHeight: "100vh !important",
          }}
        >
          <SomethingWentWrong isFetching={isFetching} onClick={refetch} />
        </div>
      ) : !permissionData ? (
        <LoadingTableData fullHeight={true} />
      ) : (
        <RedirectProvider>
          <ResponsiveDrawer>{children}</ResponsiveDrawer>
        </RedirectProvider>
      )}
      <SessionExpired open={showModal} />
      <SessionExpireAlert
        subheader={subheaderText}
        open={showExpireAlertModal}
        handleClose={handleCloseExpireAlert}
      />
    </>
  );
};

export default LoggedInProvider;
